import { DateTime } from 'luxon'

import { POneEvent, User } from '@modules/auth/hooks/types'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import chatApi from '@modules/chat/api'
import { isTodayDate } from '@shared/utils'
import { InfiniteData, useInfiniteQuery, useQuery } from '@tanstack/react-query'

import contactsApi from '../api'
import { StreamMessagesResponse } from '../api/contacts'
import { MessageStreamItem } from '../types'

const DEFAULT_PAGE_SIZE = 10

export interface SanitizedPages {
  messageStreamItems: MessageStreamItem[]
  hasMore: boolean
  total: number
  subject: string
}

const createSanitizeResponsePages = (user: User) => {
  return (data: InfiniteData<StreamMessagesResponse>): SanitizedPages => {
    const streamItems = (data.pages ?? []).flatMap(i => i.result)

    const total = data.pages?.[0]?.total ?? 0
    const subject = streamItems?.[0]?.subject

    const messageStreamItems: Array<MessageStreamItem> = streamItems.reduce((acc, messageDto) => {
      /**
       * Taking status update data if it is there
       */
      if (messageDto.actionType) {
        const messageItem: MessageStreamItem = {
          type: 'status',
          item: {
            id: messageDto.id,
            date: DateTime.fromISO(messageDto.createdAt).toFormat('MMM dd, yyyy'),
            status: messageDto.actionType as string,
            authorId: messageDto?.sender?.id,
          },
        }

        acc.push(messageItem)
      }

      /**
       * Taking message data if it is there
       */
      if (messageDto.sender && (messageDto.body || !messageDto.actionType)) {
        const { sender } = messageDto

        const author = [sender.title, sender.firstName, sender.middleName, sender.lastName].filter(Boolean).join(' ')

        const authorInitials = [sender.firstName, sender.lastName]
          .filter(Boolean)
          .map(word => word[0])
          .join('')

        const formattedDate = DateTime.fromISO(messageDto.createdAt).toFormat('MMM dd, yyyy')

        const messageItem: MessageStreamItem = {
          type: 'message',
          item: {
            id: messageDto.id,
            author,
            authorInitials,
            authorId: sender.id,
            date: formattedDate,
            companyName: sender.companyName,
            avatarSrc: sender.profilePicture,
            messageText: messageDto.body,
            subject: messageDto.subject,
            isMine: sender.id === user.id,
            isHidden: sender.isHidden,
          },
        }

        acc.push(messageItem)
      } else if (messageDto.body) {
        const formattedDate = DateTime.fromISO(messageDto.createdAt).toFormat('MMM dd, yyyy')
        const messageItem: MessageStreamItem = {
          type: 'message',
          item: {
            id: messageDto.id,
            isAdmin: true,
            date: formattedDate,
            messageText: messageDto.body,
            subject: messageDto.subject,
            isMine: false,
            isHidden: false,
          },
        }

        acc.push(messageItem)
      }

      return acc
    }, [] as MessageStreamItem[])

    const hasMore = total > DEFAULT_PAGE_SIZE * (data.pageParams as unknown as number) + streamItems.length

    return {
      messageStreamItems,
      hasMore,
      total,
      subject,
    }
  }
}

const useMeetingMessages = ({ eventId, streamId }: { eventId: POneEvent['id']; streamId: string }) => {
  const { user } = useAuthContext()

  return useInfiniteQuery<StreamMessagesResponse, unknown, SanitizedPages>({
    queryKey: [contactsApi.getStreamMessages.key, eventId, streamId],
    queryFn: ({ pageParam = 0 }) =>
      contactsApi.getStreamMessages.fn({
        page: pageParam as number,
        pageSize: DEFAULT_PAGE_SIZE,
        eventId,
        streamId,
      }),

    getNextPageParam: (lastPage, allPages) => {
      const allPagesLength = allPages.flatMap(i => i.result).length
      const pagesTotal = lastPage.total

      if (pagesTotal > allPagesLength) {
        return allPages.length
      }

      return undefined
    },
    initialPageParam: 0,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore:next-line 3d party lib ts issues (no error)
    select: createSanitizeResponsePages(user as User),
    enabled: Boolean(streamId && eventId),
  })
}

export const useChatMessages = ({ chatId }: { chatId?: number }) => {
  const { user } = useAuthContext()

  const { data: { members, messages, total } = {} } = useQuery({
    queryKey: [chatApi.getChatMessages.key, 'SINGLE', chatId],
    queryFn: () => chatApi.getChatMessages.fn({ chatId: chatId as number, size: 1 }),
    enabled: !!chatId && chatId !== -1,
  })

  return {
    messageStreamItems:
      messages?.map(message => {
        const author = members?.find(member => member.id === message.p1UserId)
        const isTodayMessage = isTodayDate(message.createdAt)

        const messageItem: MessageStreamItem = {
          type: 'message',
          item: {
            id: message.id,
            author: `${author?.firstName} ${author?.lastName}`,
            authorInitials: `${author?.firstName?.[0]}${author?.lastName?.[0]}`,
            authorId: author?.id as number,
            date: DateTime.fromISO(message.createdAt).toFormat(isTodayMessage ? 'T' : 'MMM dd, yyyy'),
            companyName: '',
            avatarSrc: author?.profilePicture || '',
            messageText: message.content,
            subject: '',
            isMine: author?.id === user?.id,
            isHidden: author?.isHidden ?? false,
          },
        }

        return messageItem
      }) ?? ([] as MessageStreamItem[]),
    hasMore: false,
    total,
    subject: '',
  }
}

export default useMeetingMessages
